import { createStyles, makeStyles } from '@material-ui/styles';
import * as classNames from 'classnames';
import * as React from 'react';
import { FC } from 'react';
import mediaQueries from 'src/constants/responsive';
import spacing from 'src/constants/spacing';
import stylesheet, { secondaryColor } from 'src/constants/stylesheet';
import { getAlt, getLegend } from 'src/utils/legendAlt';
import ViewMonitor from 'src/utils/ViewMonitor';
import Image from "../Image";

const useStyles = makeStyles(() =>
    createStyles({
        panelTextImageContainer: {
            width: '100%',
            display: 'flex',
            flexWrap: 'wrap',
            [mediaQueries.Mobile]: {
                width: `100%`,
                paddingBottom: spacing.Gutter.Mobile / 2,
            },
            [mediaQueries.Tablet]: {
                paddingBottom: spacing.Gutter.Tablet / 2,
            },
            [mediaQueries.Laptop]: {
                paddingBottom: spacing.Gutter.Laptop / 2,
            },
            [mediaQueries.Desktop]: {
                paddingBottom: spacing.Gutter.Desktop / 2,
            },
            [mediaQueries.BigDesktop]: {
                width: `${2 / 3 * 100}%`,
                marginLeft: 'auto',
                paddingBottom: spacing.Gutter.BigDesktop / 2,
            }
        },
        sectionPaddingBottom: {

            [mediaQueries.Mobile]: {
                paddingBottom: spacing.Gutter.Mobile,
            },
            [mediaQueries.Tablet]: {
                paddingBottom: spacing.Gutter.Tablet,
            },
            [mediaQueries.AfterMobile]: {
                paddingBottom: spacing.Gutter.Desktop,
            }
        },
        flexContainer: {
            display: 'flex',
            flexWrap: 'wrap',
        },
        flexContainerMargin: {
            [mediaQueries.Mobile]: {
                marginBottom: spacing.Gutter.Mobile,
                '&:last-child': {
                    marginBottom: 0
                }
            },
            [mediaQueries.Tablet]: {
                marginBottom: spacing.Gutter.Tablet,
                '&:last-child': {
                    marginBottom: 0
                }
            },
            [mediaQueries.AfterMobile]: {
                marginBottom: spacing.Gutter.Desktop,
                '&:last-child': {
                    marginBottom: 0
                }
            },
        },
        column: {
            [mediaQueries.Mobile]: {
                width: `100%`,
                paddingLeft: spacing.GutterGrid.Mobile / 2,
                paddingRight: spacing.GutterGrid.Mobile / 2,
            },
            [mediaQueries.Tablet]: {
                width: `50%`,
                paddingLeft: spacing.GutterGrid.Tablet / 2,
                paddingRight: spacing.GutterGrid.Tablet / 2,
            },
            [mediaQueries.Laptop]: {
                width: `50%`,
                paddingLeft: spacing.GutterGrid.Laptop / 2,
                paddingRight: spacing.GutterGrid.Laptop / 2,
            },
            [mediaQueries.Desktop]: {
                width: `50%`,
                paddingLeft: spacing.GutterGrid.Desktop / 2,
                paddingRight: spacing.GutterGrid.Desktop / 2,
            },
            [mediaQueries.BigDesktop]: {
                width: `50%`,
                paddingLeft: spacing.GutterGrid.BigDesktop / 2,
                paddingRight: spacing.GutterGrid.BigDesktop / 2,
            }
        },
        columnText: {
            [mediaQueries.Mobile]: {
                paddingBottom: spacing.GutterGrid.Mobile,
            },
        },
        imageContainer: {
            position: 'relative',
            height: 0,
            overflow: 'hidden'
        },
        image: {
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%'
        },
        imageLegend: {
            color: secondaryColor,
            ...stylesheet.Caption,

            [mediaQueries.Mobile]: {
                marginTop: spacing.GutterGrid.Mobile / 3,
            },
            [mediaQueries.Tablet]: {
                marginTop: spacing.GutterGrid.Tablet / 3,
            },
            [mediaQueries.Laptop]: {
                marginTop: spacing.GutterGrid.Laptop / 3,
            },
            [mediaQueries.Desktop]: {
                marginTop: spacing.GutterGrid.Desktop / 3,
            },
            [mediaQueries.BigDesktop]: {
                marginTop: spacing.GutterGrid.BigDesktop / 3,
            }
        },
        readMoreContent: {
            width: `100%`,
            [mediaQueries.Mobile]: {
                paddingLeft: spacing.GutterGrid.Mobile / 2,
                paddingRight: spacing.GutterGrid.Mobile / 2,
            },
            [mediaQueries.Tablet]: {
                paddingLeft: spacing.GutterGrid.Tablet / 2,
                paddingRight: spacing.GutterGrid.Tablet / 2,
            },
            [mediaQueries.Laptop]: {
                paddingLeft: spacing.GutterGrid.Laptop / 2,
                paddingRight: spacing.GutterGrid.Laptop / 2,
            },
            [mediaQueries.Desktop]: {
                paddingLeft: spacing.GutterGrid.Desktop / 2,
                paddingRight: spacing.GutterGrid.Desktop / 2,
            },
            [mediaQueries.BigDesktop]: {
                paddingLeft: spacing.GutterGrid.BigDesktop / 2,
                paddingRight: spacing.GutterGrid.BigDesktop / 2,
            }
        },
        hideText: {
            ...stylesheet.Hide,
            transitionDelay: '100ms'
        },
        hideImage: {
            ...stylesheet.Hide,
            transitionDelay: '200ms'
        },
        show: {
            ...stylesheet.Show
        },
        imageOuter:{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
        }
    }));

export type PanelTextImageProps = PanelTextImageDto;

type Props = PanelTextImageProps;

const PanelTextImage: FC<Props> = ({ children, Text: text, Image: image }) => {
    const cls = useStyles();

    const sizes = "(max-width: 768px) 50vw, (min-width: 769px) and (max-width: 1699px) 33vw, (min-width: 1700px) 25vw";

    return (
        <div className={cls.panelTextImageContainer}>
            <ViewMonitor once={true}>{(intersect) =>
                <div
                    className={classNames(cls.column, cls.columnText, cls.hideText, intersect && cls.show)}
                    dangerouslySetInnerHTML={{ __html: text }}
                />
            }</ViewMonitor>
            <ViewMonitor once={true}>{(intersect) =>
                <div className={classNames(cls.column, cls.hideImage, intersect && cls.show)}>
                    <div
                        className={cls.imageContainer}
                        style={{ paddingBottom: `${image.Height / image.Width * 100}%` }}
                    >
                        <div className={cls.imageOuter}>
                            <Image
                                pictureRef={image}
                                sizes={sizes}
                                className={cls.image}
                                alt={getAlt(image)}
                            />
                        </div>
                    </div>
                    {image.Legend &&
                        <figcaption
                            className={cls.imageLegend}
                            dangerouslySetInnerHTML={{
                                __html: getLegend(image)
                            }}
                        />
                    }
                </div>
            }</ViewMonitor>
            <div className={cls.readMoreContent}>
                {children}
            </div>
        </div>
    );
};

export default PanelTextImage;



// WEBPACK FOOTER //
// ./src/Components/Panels/PanelTextImage.tsx